import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Phone, FloppyDisk, Eye, EyeSlash, CheckCircle, Warning, Spinner, ArrowsClockwise, LinkSimple, Globe, Stack, ChatCircle } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import AccountMenu from "@/components/AccountMenu";
import { useT } from "@/lib/i18n";

/**
 * TwilioCredentialsPage — super-admin write, broader staff read masked.
 *
 * Mirrors the existing DBConfigPage pattern but for Twilio creds. The
 * Mongo `twilio_credentials` collection (singleton _id="active") overrides
 * .env at runtime, so rotating creds here takes effect instantly without
 * a backend restart.
 */
export default function TwilioCredentialsPage({ user, onLogout, onLogoutAll }) {
  const navigate = useNavigate();
  const tx = useT();
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [error, setError] = useState("");
  const [edits, setEdits] = useState({}); // {FIELD: newValue}
  const [reveal, setReveal] = useState({}); // {FIELD: bool}

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const r = await api.get("/admin/twilio-credentials");
      setMeta(r.data);
      setEdits({});
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Could not load Twilio credentials");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onSave = async () => {
    if (!Object.keys(edits).length) return;
    if (!window.confirm(`Save Twilio credentials change?\n\nFields:\n${Object.keys(edits).join("\n")}`)) return;
    setSaving(true); setError("");
    try {
      await api.put("/admin/twilio-credentials", { fields: edits });
      await load();
      setTestResult(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const r = await api.post("/admin/twilio-credentials/test");
      setTestResult(r.data);
    } catch (e) {
      setTestResult({ ok: false, message: e?.response?.data?.detail || e?.message });
    } finally {
      setTesting(false);
    }
  };

  const labelMap = {
    TWILIO_ACCOUNT_SID: { label: "Account SID", hint: "Starts with AC… — found on Twilio Console dashboard top-right." },
    TWILIO_AUTH_TOKEN:  { label: "Auth Token",  hint: "32-char hex secret — found next to Account SID on the dashboard." },
    TWILIO_API_KEY_SID: { label: "API Key SID", hint: "Starts with SK… — Account → API keys & tokens → Standard key." },
    TWILIO_API_KEY_SECRET: { label: "API Key Secret", hint: "Long random string — shown ONCE at API key creation. Used to mint Video Access Tokens." },
    TWILIO_VERIFY_SERVICE_SID: { label: "Verify Service SID", hint: "Starts with VA… — Verify → Services → Service Configuration." },
    TWILIO_PHONE_NUMBER: { label: "Phone Number (Sender)", hint: "E.164 format, e.g. +18257860229 — appears as the From number on all outbound SMS." },
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 md:px-8 h-14 flex items-center gap-3">
          <button onClick={() => navigate("/")} className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500" title={tx("action.back", "Back")}>
            <ArrowLeft size={18} weight="bold" />
          </button>
          <Phone size={20} weight="fill" className="text-blue-700" />
          <h1 className="text-base md:text-lg font-black tracking-tighter font-['Chivo']">
            {tx("twilio_creds.title", "Twilio Credentials")}
          </h1>
          <button onClick={load} disabled={loading}
            className={`ml-2 w-7 h-7 flex items-center justify-center rounded-sm hover:bg-slate-100 ${loading ? "text-blue-600" : "text-slate-500"}`}
            title={loading ? "Reloading…" : "Reload"} data-testid="twilio-creds-reload-btn">
            <ArrowsClockwise size={14} weight="bold" className={loading ? "animate-spin" : ""} />
          </button>
          <div className="ml-auto">
            <AccountMenu user={user} onLogout={onLogout} onLogoutAll={onLogoutAll} />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 md:p-8">
        <div className="mb-4">
          <p className="text-sm text-slate-600">
            {tx("twilio_creds.description", "These credentials power Video calls, SMS, and the Verify (OTP) flow. Mongo overrides take effect")} <strong>{tx("payment_credentials.instantly", "instantly")}</strong> — {tx("payment_credentials.no_restart", "no restart needed.")} {tx("twilio_creds.empty_fallback", "Empty values fall back to")} <code className="text-[11px] bg-slate-100 px-1 rounded">.env</code>.
          </p>
          {meta && !meta.can_edit && (
            <div className="mt-3 text-[11px] font-bold uppercase tracking-wider text-amber-800 bg-amber-50 border border-amber-200 px-2 py-1 rounded-sm inline-flex items-center gap-1">
              <ShieldCheck size={11} weight="bold" /> Read-only — super-admin required to edit
            </div>
          )}
          {meta?.last_updated_at && (
            <div className="mt-2 text-[10px] text-slate-400">
              {tx("payment_credentials.last_updated_by", "Last updated by")} <span className="font-mono">{meta.last_updated_by}</span> {tx("payment_credentials.at_time", "at")} {new Date(meta.last_updated_at).toLocaleString()}
            </div>
          )}
        </div>

        {error && (
          <div className="mb-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-sm p-3" data-testid="twilio-creds-error">
            {error}
          </div>
        )}

        {loading && !meta ? (
          <div className="flex items-center gap-2 text-sm text-slate-500"><Spinner size={16} className="animate-spin" /> Loading…</div>
        ) : meta && (
          <div className="border border-slate-200 rounded-md divide-y divide-slate-100 bg-white">
            {meta.fields.map((f) => {
              const lbl = labelMap[f.name] || { label: f.name, hint: "" };
              const isRevealed = !!reveal[f.name];
              const newVal = edits[f.name];
              const editing = newVal !== undefined;
              return (
                <div key={f.name} className="p-4">
                  <div className="flex items-center justify-between mb-1">
                    <div>
                      <div className="text-sm font-bold text-slate-900">{lbl.label}</div>
                      <div className="text-[10px] font-mono text-slate-400">{f.name}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${
                        f.source === "mongo_override" ? "bg-blue-50 text-blue-700 border-blue-200" :
                        f.source === "env" ? "bg-slate-100 text-slate-600 border-slate-200" :
                        "bg-rose-50 text-rose-700 border-rose-200"}`}>
                        {f.source === "mongo_override" ? "Override" : f.source === "env" ? "From .env" : "Missing"}
                      </span>
                      {f.has_value && (
                        <button onClick={() => setReveal((r) => ({ ...r, [f.name]: !r[f.name] }))}
                          className="text-slate-400 hover:text-slate-700"
                          title={isRevealed ? "Hide" : "Show masked value"}
                          data-testid={`twilio-cred-reveal-${f.name}`}>
                          {isRevealed ? <EyeSlash size={14} /> : <Eye size={14} />}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="text-[11px] text-slate-500 mb-2">{lbl.hint}</div>
                  <div className="flex gap-2">
                    <Input
                      data-testid={`twilio-cred-input-${f.name}`}
                      type={isRevealed && !editing ? "text" : (editing ? "text" : "password")}
                      value={editing ? newVal : (isRevealed ? f.active_masked : (f.has_value ? f.active_masked : ""))}
                      onChange={(e) => setEdits((p) => ({ ...p, [f.name]: e.target.value }))}
                      placeholder={f.has_value ? f.active_masked : "(not set)"}
                      readOnly={!meta.can_edit}
                      className="flex-1 font-mono text-xs h-9"
                    />
                    {editing && (
                      <button onClick={() => setEdits((p) => { const n = { ...p }; delete n[f.name]; return n; })}
                        data-testid={`twilio-cred-cancel-${f.name}`}
                        className="text-[11px] font-bold uppercase tracking-wider text-slate-500 hover:text-rose-600 px-2">
                        {tx("action.cancel", "Cancel")}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {meta?.can_edit && (
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <button onClick={onSave}
              disabled={saving || Object.keys(edits).length === 0}
              data-testid="twilio-creds-save-btn"
              className="inline-flex items-center gap-1.5 h-9 px-4 bg-blue-700 hover:bg-blue-800 text-white text-xs font-bold uppercase tracking-wider rounded-sm disabled:opacity-50">
              {saving ? <Spinner size={12} className="animate-spin" /> : <FloppyDisk size={12} weight="fill" />}
              {tx("action.save", "Save")} {Object.keys(edits).length > 0 && `(${Object.keys(edits).length})`}
            </button>
            <button onClick={onTest} disabled={testing}
              data-testid="twilio-creds-test-btn"
              className="inline-flex items-center gap-1.5 h-9 px-4 border border-slate-300 hover:bg-slate-50 text-xs font-bold uppercase tracking-wider rounded-sm disabled:opacity-50">
              {testing ? <Spinner size={12} className="animate-spin" /> : <CheckCircle size={12} weight="fill" />}
              {tx("payment_credentials.test_connection", "Test connection")}
            </button>
            {testResult && (
              <div className={`text-xs font-medium px-2 py-1 rounded-sm border ${testResult.ok ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"}`}
                data-testid="twilio-creds-test-result">
                {testResult.ok ? (
                  <span className="inline-flex items-center gap-1">
                    <CheckCircle size={12} weight="fill" /> Connected as <strong>{testResult.friendly_name || testResult.account_sid}</strong> · {testResult.status}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1">
                    <Warning size={12} weight="fill" /> {testResult.message}
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </main>

      {meta?.can_edit && <WebhookConfigPanel />}
      {meta?.can_edit && <MessagingServicesPanel />}
    </div>
  );
}

// ─── Webhook Config Sync Panel ──────────────────────────────────────


function WebhookConfigPanel() {
  const tx = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(""); setSyncResult(null);
    try {
      const r = await api.get("/admin/twilio-credentials/webhook-config");
      setData(r.data);
      setSelected(new Set());
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Could not load webhook config");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = (sid) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(sid)) n.delete(sid); else n.add(sid);
    return n;
  });

  const onSync = async () => {
    if (!selected.size) return;
    const phones = (data?.items || []).filter((x) => selected.has(x.sid)).map((x) => x.phone_number);
    if (!window.confirm(
      `Update SMS webhook for these ${selected.size} number(s)?\n\n${phones.join("\n")}\n\nThis will overwrite their current SMS webhook URL with:\n${data.expected.sms_inbound}`
    )) return;
    setSyncing(true); setSyncResult(null);
    try {
      const r = await api.post("/admin/twilio-credentials/webhook-config/sync", {
        phone_sids: Array.from(selected),
        set_status_callback: true,
      });
      setSyncResult(r.data);
      await load();
    } catch (e) {
      setSyncResult({ failed: [{ error: e?.response?.data?.detail || e?.message || "Sync failed" }] });
    } finally {
      setSyncing(false);
    }
  };

  const items = data?.items || [];
  const outOfSync = items.filter((x) => !x.in_sync);
  const inSync = items.filter((x) => x.in_sync);

  return (
    <main className="max-w-4xl mx-auto px-4 md:px-8 pb-12" data-testid="twilio-webhook-panel">
      <div className="mt-2 mb-4 flex items-baseline gap-2 flex-wrap">
        <h2 className="text-lg font-bold tracking-tight font-['Chivo']">{tx("twilio_creds.inbound_sms_webhooks", "Inbound SMS webhooks")}</h2>
        <span className="text-xs text-slate-500">({items.length} numbers)</span>
        <button onClick={load} disabled={loading} className="ml-auto inline-flex items-center gap-1 text-xs px-2 py-1 border border-slate-300 rounded-sm hover:bg-slate-100 disabled:opacity-50" data-testid="webhook-refresh">
          {loading ? <Spinner size={12} className="animate-spin" /> : <ArrowsClockwise size={12} />}
          {tx("action.refresh", "Refresh")}
        </button>
      </div>

      {data?.expected?.base && (
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 mb-3 text-xs">
          <div className="flex items-center gap-2 text-slate-700 mb-1">
            <Globe size={12} weight="fill" />
            <span className="font-bold">{tx("twilio_creds.canonical_url", "Canonical webhook URL (this app)")}</span>
          </div>
          <div className="font-mono text-blue-700 break-all" data-testid="webhook-canonical-url">{data.expected.sms_inbound}</div>
          <div className="font-mono text-slate-500 break-all">{data.expected.sms_status_callback}</div>
        </div>
      )}

      <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 mb-3 text-xs flex items-start gap-2">
        <Warning size={16} weight="fill" className="text-amber-700 shrink-0 mt-0.5" />
        <div className="text-amber-900 leading-relaxed">
          <strong>{tx("twilio_creds.read_warning_title", "Read this before clicking Sync.")}</strong>{tx("twilio_creds.read_warning_body", " Many of your numbers point at the legacy")}
          <span className="font-mono mx-1 px-1 bg-amber-100 rounded-sm">theusaircompressor.com</span>
          PHP system. Re-pointing those numbers will <em>break</em> the PHP integration. Only sync the numbers you intentionally
          want this React app to handle.
        </div>
      </div>

      {error && <div className="bg-rose-50 border border-rose-200 text-rose-800 text-sm rounded-sm px-3 py-2 mb-3">{String(error)}</div>}

      {syncResult && (
        <div className="bg-white border border-slate-200 rounded-sm p-3 mb-3 text-xs space-y-1" data-testid="webhook-sync-result">
          {syncResult.updated?.length > 0 && (
            <div className="text-emerald-700"><CheckCircle size={11} weight="fill" className="inline-block mr-1" /> Updated: {syncResult.updated.map((x) => x.phone_number).join(", ")}</div>
          )}
          {syncResult.skipped?.length > 0 && (
            <div className="text-slate-500">↺ {tx("twilio_creds.already_in_sync", "Already in sync:")} {syncResult.skipped.map((x) => x.phone_number).join(", ")}</div>
          )}
          {syncResult.failed?.length > 0 && (
            <div className="text-rose-700"><Warning size={11} weight="fill" className="inline-block mr-1" /> Failed: {syncResult.failed.map((x) => `${x.phone_number || "?"}: ${x.error}`).join(", ")}</div>
          )}
        </div>
      )}

      {loading ? (
        <div className="bg-white rounded-sm border border-slate-200 p-8 text-center"><Spinner size={20} className="animate-spin text-slate-400 mx-auto mb-1" /><div className="text-xs text-slate-500">{tx("twilio_creds.loading_numbers", "Loading numbers from Twilio…")}</div></div>
      ) : (
        <>
          <NumberTable items={outOfSync} title={tx("twilio_creds.out_of_sync", "Out of sync")} tone="rose" selected={selected} onToggle={toggle} expectedUrl={data?.expected?.sms_inbound} testIdPrefix="webhook-row-out" />
          {inSync.length > 0 && <NumberTable items={inSync} title={tx("twilio_creds.pointing_here", "Already pointing here")} tone="emerald" selected={null} onToggle={null} expectedUrl={data?.expected?.sms_inbound} testIdPrefix="webhook-row-in" />}
        </>
      )}

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <button
          onClick={onSync}
          disabled={syncing || !selected.size}
          data-testid="webhook-sync-btn"
          className="inline-flex items-center gap-1.5 h-9 px-4 bg-violet-600 hover:bg-violet-700 text-white text-xs font-bold uppercase tracking-wider rounded-sm disabled:opacity-50"
        >
          {syncing ? <Spinner size={12} className="animate-spin" /> : <LinkSimple size={12} weight="fill" />}
          Sync {selected.size} selected
        </button>
        <span className="text-[11px] text-slate-500">
          {tx("twilio_creds.pointing_at", "Pointing at")} <span className="font-mono">{data?.expected?.base?.replace(/^https:\/\//, "") || "(no base url)"}</span>
        </span>
      </div>
    </main>
  );
}

function NumberTable({ items, title, tone, selected, onToggle, expectedUrl, testIdPrefix }) {
  if (!items?.length) return null;
  const toneCls = {
    rose: "border-rose-200",
    emerald: "border-emerald-200",
  }[tone] || "border-slate-200";
  const headerCls = {
    rose: "bg-rose-50 text-rose-800",
    emerald: "bg-emerald-50 text-emerald-800",
  }[tone] || "bg-slate-50 text-slate-700";
  return (
    <div className={`bg-white rounded-sm border ${toneCls} mb-3 overflow-hidden`}>
      <div className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] ${headerCls} flex items-center justify-between`}>
        <span>{title} · {items.length}</span>
        {onToggle && (
          <button
            onClick={() => {
              const allSelected = items.every((x) => selected?.has(x.sid));
              const nextSet = new Set(selected || []);
              if (allSelected) items.forEach((x) => nextSet.delete(x.sid));
              else items.forEach((x) => nextSet.add(x.sid));
              // Caller's setter takes the toggled set indirectly; emulate by toggling each.
              items.forEach((x) => {
                const wantSelected = !allSelected;
                const isSel = selected?.has(x.sid);
                if (wantSelected !== isSel) onToggle(x.sid);
              });
            }}
            className="text-[10px] font-bold uppercase tracking-wider text-slate-600 hover:text-violet-700"
            data-testid={`${testIdPrefix}-toggle-all`}
          >
            Toggle all
          </button>
        )}
      </div>
      <table className="w-full text-xs">
        <tbody>
          {items.map((x) => (
            <tr key={x.sid} className="border-t border-slate-100 hover:bg-slate-50/50">
              {onToggle && (
                <td className="px-3 py-2 w-8">
                  <input
                    type="checkbox"
                    checked={selected?.has(x.sid) || false}
                    onChange={() => onToggle(x.sid)}
                    data-testid={`${testIdPrefix}-${x.sid}-checkbox`}
                    className="cursor-pointer"
                  />
                </td>
              )}
              <td className="px-3 py-2 font-mono whitespace-nowrap">{x.phone_number}</td>
              <td className="px-3 py-2 text-slate-600 truncate max-w-[120px]">{x.friendly_name || "—"}</td>
              <td className="px-3 py-2 font-mono text-[10px] text-slate-700 break-all">
                {x.sms_url ? (
                  x.sms_url === expectedUrl
                    ? <span className="text-emerald-700">{x.sms_url}</span>
                    : <span className="text-rose-700">{x.sms_url}</span>
                ) : <span className="text-slate-300">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Messaging Services Panel ──────────────────────────────────────
//
// When a phone number is attached to a Twilio Messaging Service AND that
// service has `use_inbound_webhook_on_number=false` AND its
// `inbound_request_url` is empty, Twilio silently drops inbound SMS to our
// backend (the per-number sms_url is ignored). This panel surfaces every
// service's routing config and offers a one-click fix.

function MessagingServicesPanel() {
  const tx = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(null); // service_sid being updated
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(""); setResult(null);
    try {
      const r = await api.get("/admin/twilio-credentials/messaging-services");
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Could not load messaging services");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onFix = async (svc, mode) => {
    const useNumber = mode === "per-number";
    const msg = useNumber
      ? `Set this service to honor each phone number's individual SMS webhook?\n\n` +
        `Service: ${svc.friendly_name}\n` +
        `Numbers attached: ${svc.phone_numbers.length}\n\n` +
        `Inbound to numbers wired to this app will start delivering. Numbers ` +
        `still pointing at legacy PHP will keep doing so.`
      : `Set this service's inbound webhook directly to:\n${data.expected.sms_inbound}\n\n` +
        `Service: ${svc.friendly_name}\n` +
        `Numbers attached: ${svc.phone_numbers.length}\n\n` +
        `EVERY number on this service will route to our backend, regardless ` +
        `of its per-number config. Use this when you want this app to handle ALL ` +
        `numbers in the service.`;
    if (!window.confirm(msg)) return;
    setBusy(svc.sid); setResult(null);
    try {
      const r = await api.post("/admin/twilio-credentials/messaging-services/fix", {
        service_sid: svc.sid,
        use_inbound_webhook_on_number: useNumber,
      });
      setResult({ ok: true, service: r.data });
      await load();
    } catch (e) {
      setResult({ ok: false, error: e?.response?.data?.detail || e?.message || "Fix failed" });
    } finally {
      setBusy(null);
    }
  };

  const items = data?.items || [];

  return (
    <main className="max-w-4xl mx-auto px-4 md:px-8 pb-12" data-testid="twilio-msgsvc-panel">
      <div className="mt-2 mb-4 flex items-baseline gap-2 flex-wrap">
        <ChatCircle size={16} weight="fill" className="text-violet-700" />
        <h2 className="text-lg font-bold tracking-tight font-['Chivo']">{tx("twilio_creds.messaging_services", "Messaging Services")}</h2>
        <span className="text-xs text-slate-500">({items.length} services)</span>
        <button onClick={load} disabled={loading}
                data-testid="msgsvc-refresh"
                className="ml-auto inline-flex items-center gap-1 text-xs px-2 py-1 border border-slate-300 rounded-sm hover:bg-slate-100 disabled:opacity-50">
          {loading ? <Spinner size={12} className="animate-spin" /> : <ArrowsClockwise size={12} />}
          {tx("action.refresh", "Refresh")}
        </button>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 mb-3 text-xs flex items-start gap-2">
        <Warning size={16} weight="fill" className="text-amber-700 shrink-0 mt-0.5" />
        <div className="text-amber-900 leading-relaxed">
          <strong>{tx("twilio_creds.why_exists_title", "Why this exists.")}</strong>{tx("twilio_creds.why_exists_body", " When a number belongs to a Messaging Service, Twilio")}
          ignores its per-number SMS webhook unless the service has
          <span className="font-mono mx-1 px-1 bg-amber-100 rounded-sm">use_inbound_webhook_on_number = true</span>
          OR the service's own
          <span className="font-mono mx-1 px-1 bg-amber-100 rounded-sm">inbound_request_url</span>
          is set. Otherwise inbound SMS is captured by the service (typically into the Conversations
          API) and never reaches our backend.
        </div>
      </div>

      {error && <div className="bg-rose-50 border border-rose-200 text-rose-800 text-sm rounded-sm px-3 py-2 mb-3">{String(error)}</div>}

      {result && (
        <div data-testid="msgsvc-fix-result"
             className={`rounded-sm px-3 py-2 mb-3 text-xs border ${
               result.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                         : "bg-rose-50 border-rose-200 text-rose-800"}`}>
          {result.ok ? (
            <span><CheckCircle size={11} weight="fill" className="inline-block mr-1" />
              Updated <strong>{result.service.friendly_name}</strong> · use_inbound_webhook_on_number=
              <code className="font-mono">{String(result.service.use_inbound_webhook_on_number)}</code>
              {result.service.inbound_request_url && (
                <> · inbound_request_url=<code className="font-mono break-all">{result.service.inbound_request_url}</code></>
              )}
            </span>
          ) : <span><Warning size={11} weight="fill" className="inline-block mr-1" /> {result.error}</span>}
        </div>
      )}

      {loading ? (
        <div className="bg-white rounded-sm border border-slate-200 p-8 text-center">
          <Spinner size={20} className="animate-spin text-slate-400 mx-auto mb-1" />
          <div className="text-xs text-slate-500">Loading messaging services from Twilio…</div>
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-sm border border-slate-200 p-6 text-center text-xs text-slate-500">
          No Messaging Services on this Twilio account.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((svc) => {
            const tone = svc.is_drifting
              ? "border-rose-300 bg-rose-50/30"
              : "border-emerald-200 bg-white";
            return (
              <div key={svc.sid} data-testid={`msgsvc-row-${svc.sid}`}
                   className={`rounded-sm border ${tone} p-3`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Stack size={13} weight="bold" className="text-slate-500" />
                      <span className="font-bold text-sm">{svc.friendly_name}</span>
                      {svc.is_drifting ? (
                        <span className="text-[9px] uppercase font-bold tracking-wider bg-rose-600 text-white px-1.5 py-0.5 rounded-sm">
                          Inbound dropped
                        </span>
                      ) : (
                        <span className="text-[9px] uppercase font-bold tracking-wider bg-emerald-600 text-white px-1.5 py-0.5 rounded-sm">
                          OK
                        </span>
                      )}
                      <span className="text-[10px] font-mono text-slate-400">{svc.sid}</span>
                    </div>
                    <div className="text-xs text-slate-600 mt-2 space-y-0.5">
                      <div>
                        <span className="font-mono text-slate-500">use_inbound_webhook_on_number:</span>{" "}
                        <code className={`font-mono px-1 rounded-sm ${svc.use_inbound_webhook_on_number ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-700"}`}>
                          {String(svc.use_inbound_webhook_on_number)}
                        </code>
                      </div>
                      <div className="break-all">
                        <span className="font-mono text-slate-500">inbound_request_url:</span>{" "}
                        {svc.inbound_request_url ? (
                          <code className={`font-mono ${svc.inbound_request_url === data.expected.sms_inbound ? "text-emerald-700" : "text-rose-700"}`}>
                            {svc.inbound_request_url}
                          </code>
                        ) : <span className="text-slate-400 italic">(empty)</span>}
                      </div>
                      <div>
                        <span className="font-mono text-slate-500">phone_numbers:</span>{" "}
                        <span className="text-slate-700">{svc.phone_numbers.length}</span>
                        {svc.phone_numbers.length > 0 && (
                          <span className="text-slate-400 ml-1">({svc.phone_numbers.slice(0, 3).map(p => p.phone_number).join(", ")}{svc.phone_numbers.length > 3 ? ` +${svc.phone_numbers.length - 3} more` : ""})</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5 shrink-0">
                    <button
                      onClick={() => onFix(svc, "per-number")}
                      disabled={busy === svc.sid}
                      data-testid={`msgsvc-fix-per-number-${svc.sid}`}
                      title="Honor each phone number's individual SMS webhook (recommended when most numbers are already wired to this app)"
                      className="inline-flex items-center gap-1 h-7 px-3 bg-violet-600 hover:bg-violet-700 text-white text-[11px] font-bold uppercase tracking-wider rounded-sm disabled:opacity-50"
                    >
                      {busy === svc.sid ? <Spinner size={11} className="animate-spin" /> : <LinkSimple size={11} weight="bold" />}
                      Use per-number webhooks
                    </button>
                    <button
                      onClick={() => onFix(svc, "service")}
                      disabled={busy === svc.sid}
                      data-testid={`msgsvc-fix-service-${svc.sid}`}
                      title="Set inbound_request_url on the service so EVERY attached number routes to this backend"
                      className="inline-flex items-center gap-1 h-7 px-3 bg-slate-700 hover:bg-slate-900 text-white text-[11px] font-bold uppercase tracking-wider rounded-sm disabled:opacity-50"
                    >
                      <Globe size={11} weight="bold" />
                      Route ALL to backend
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </main>
  );
}
